import { useEffect, useState, useCallback, useRef } from 'react';
import { ConfirmDialog } from '@shared/ui';
import { RefreshIcon } from '@shared/ui/icons';
import { ALL_ROUTES } from '@app/router';
import { useDispatch, useSelector } from 'react-redux';
import { navigateToRoute, updateIsMaintenanceMode } from './redux_store';
import { checkIsMaintenanceMode } from '@shared/lib/downForMaintenance';
import { fetchVersionPolicy, resolveUpdateAction } from '@shared/api/versionPolicy';
import { msUntilNextMidnight } from '@shared/lib/nextMidnight';
import { hasUnsavedWork, subscribeUnsavedWork } from '@shared/lib/unsavedWork';
import { getAccountTimeZone } from '@entities/account';
import { MandatoryUpdatePage } from '@pages/mandatory-update';

// The policy endpoint is the sole source of update state
// (GET /versions/dashboard/policy). It replaced
// /system/dashboard/version, which could only answer "something
// changed" and so could not distinguish "keep working" from "update
// before continuing".
//
// Checked on mount (so: app open, reload and sign-in), whenever the tab
// regains focus, and once a night. Deliberately NOT on a short
// interval: a timer can only ever fire while someone is working, and
// the screen it may raise replaces the whole app. The two moments that
// cost a user nothing are coming back to the tab and the small hours.

// Set by MandatoryUpdatePage before it reloads. If we come back still
// below the floor (a stale CDN copy, an unfinished deploy), the screen
// says so instead of looking like a first visit — and we still never
// reload on our own, so there is no loop to fall into.
const RELOAD_ATTEMPT_KEY = 'qhs.mandatoryUpdateReload';

const readReloadAttempt = () => {
  try {
    return sessionStorage.getItem(RELOAD_ATTEMPT_KEY) === '1';
  } catch {
    return false;
  }
};

const clearReloadAttempt = () => {
  try {
    sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
};

const CacheBuster = ({ children }) => {
  const { dialogs } = useSelector(state => state.i18n);
  const dispatch = useDispatch();
  const { isMaintenanceMode, metadata } = useSelector(state => state.user);
  const accountTimeZone = getAccountTimeZone(metadata, metadata?.currentAccount?.uuid);

  // Use refs for cleanup and preventing memory leaks
  const abortControllerRef = useRef(null);

  // Optimize state structure
  const [prevIsMaintenanceMode, setPrevIsMaintenanceMode] = useState(false);
  // A newer build exists and the user may keep working. Just a flag:
  // the dialog deliberately does not name the version, so there is
  // nothing to carry alongside it.
  const [noticeAvailable, setNoticeAvailable] = useState(false);
  // "Skip for now" has to mean more than "until I switch tabs". Checks
  // run on every refocus, and without this the dialog came straight back
  // each time the user returned from their mail — all day, until they
  // updated. Deliberately NOT reset by a check, and deliberately local
  // state rather than storage: the session ends at a reload, which is
  // itself the update, so asking again then is right.
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  // Non-null once the running build is below `minimum`; holds the
  // policy so the screen can name the version the user is going to.
  //
  // It is "pending" rather than "blocked" because a block waits for any
  // unsaved work on the page to be saved or discarded: replacing the
  // app mid-note would throw the note away, and losing a clinician's
  // typing is worse than another half hour on an old build. While it
  // waits the app stays fully usable — saving included — and the user
  // sees the ordinary update notice instead, so the wait is not silent.
  const [pendingBlockPolicy, setPendingBlockPolicy] = useState(null);
  const [unsavedWork, setUnsavedWorkState] = useState(() => hasUnsavedWork());
  // The deferred notice has to be dismissable, or the user cannot reach
  // the form it is asking them to save. Dismissing hides the dialog, not
  // the block: the blocking screen still appears the moment nothing is
  // unsaved any more.
  const [deferredNoticeDismissed, setDeferredNoticeDismissed] = useState(false);

  // Purges the cached build and reloads. It deliberately leaves storage
  // alone: it used to clear localStorage and sessionStorage too, which
  // drops the sign-in flag and lands the user on the login page. The
  // MANDATORY screen has never done that, so volunteering to update cost
  // more than being forced to — the wrong way round. What has to go is
  // the cached build, and that lives in the Cache API.
  const refreshCacheAndReload = useCallback(async () => {
    try {
      if ('caches' in window) {
        const cacheNames = await window.caches.keys();
        await Promise.all(cacheNames.map(cacheName => window.caches.delete(cacheName)));
      }
    } catch (error) {
      // A refused cache API (private mode, storage policy) is not a
      // reason to strand the user — reload regardless.
      console.warn('Failed to clear caches before reload:', error);
    }
    window.location.reload(true);
  }, []);

  useEffect(() => subscribeUnsavedWork(setUnsavedWorkState), []);

  const checkVersion = useCallback(async () => {
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // fetchVersionPolicy never throws: any failure (offline, timeout,
    // 5xx, unparseable body, endpoint not deployed yet) comes back as
    // all-nulls, which resolves to 'none'. Failing open is the point —
    // a config mistake or a network blip must not lock anyone out.
    const policy = await fetchVersionPolicy({ signal });
    if (signal.aborted) return;
    abortControllerRef.current = null;

    const action = resolveUpdateAction(process.env.REACT_APP_BUILD_NUMBER, policy);

    if (action === 'block') {
      setPendingBlockPolicy(policy);
      // Every check re-raises the notice: dismissing it buys time to
      // save, it does not opt out of the update.
      setDeferredNoticeDismissed(false);
      return;
    }

    // Recovered (the floor was lowered, or this tab now runs a new
    // build): drop the block and the retry marker.
    setPendingBlockPolicy(null);
    clearReloadAttempt();

    // Also the only place the notice is withdrawn: a check that comes
    // back 'none' means there is nothing newer any more (the floor was
    // lowered, or this tab is already on the current build), so a
    // dialog still on screen is stale.
    setNoticeAvailable(action === 'notice');
  }, []);

  const checkMaintenance = useCallback(async () => {
    try {
      const response = await checkIsMaintenanceMode();
      const isMaintenanceActive = response.status === 200;

      dispatch(updateIsMaintenanceMode(isMaintenanceActive));
      setPrevIsMaintenanceMode(isMaintenanceActive);
    } catch (error) {
      console.error('Failed to check maintenance mode:', error);
      // On error, assume not in maintenance mode
      dispatch(updateIsMaintenanceMode(false));
      setPrevIsMaintenanceMode(false);
    }
  }, [dispatch]);

  useEffect(() => {
    // Mount covers opening the app, reloading it and signing in — the
    // provider tree remounts on each.
    checkMaintenance();
    checkVersion();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [checkMaintenance, checkVersion]);

  // Once a night, in the ACCOUNT's zone rather than the browser's:
  // midnight in New York is nine in the evening in California, which is
  // still someone's working day. Before sign-in there is no account, so
  // the shared default stands in. Re-armed after every run, so a tab
  // left open for a week keeps checking.
  useEffect(() => {
    let timeoutId = null;
    const arm = () => {
      timeoutId = setTimeout(() => {
        checkVersion();
        arm();
      }, msUntilNextMidnight(accountTimeZone));
    };
    arm();
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [checkVersion, accountTimeZone]);

  // A tab left open all night would otherwise wait out the interval
  // before noticing a raised floor; re-check the moment it comes back.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [checkVersion]);

  useEffect(() => {
    if (isMaintenanceMode && prevIsMaintenanceMode === false) {
      redirectToDownForMaintenance();
    }
  }, [isMaintenanceMode, prevIsMaintenanceMode]);

  const redirectToDownForMaintenance = useCallback(() => {
    dispatch(
      navigateToRoute({
        route: {
          path: ALL_ROUTES.downForMaintenance.path,
        },
      })
    );
  }, [dispatch]);

  // One button closes two different dialogs, and they forget differently:
  // skipping an optional update lasts the session, while dismissing a
  // deferred block only buys time to save — every later check raises it
  // again.
  const closeModal = useCallback(() => {
    setNoticeDismissed(true);
    setDeferredNoticeDismissed(true);
  }, []);

  // Below the floor: the app is not rendered at all, so there is no
  // route, dialog or keyboard shortcut that reaches it. The optional
  // dialog is deliberately not rendered alongside — one instruction at
  // a time.
  //
  // Unless something on the page is unsaved. Then the block waits: the
  // app keeps working, saving included, and the ordinary notice carries
  // the message instead. The next check — tab focus, the nightly one,
  // or the next sign-in — applies it, by which time the work is
  // usually saved.
  if (pendingBlockPolicy && !unsavedWork) {
    return (
      <MandatoryUpdatePage
        currentName={pendingBlockPolicy.currentName}
        attemptedReload={readReloadAttempt()}
      />
    );
  }

  // A deferred block and an ordinary notice use the same dialog but say
  // different things. "There is a newer version, click Update" is wrong
  // when the update is required and the only thing holding it back is
  // the user's own unsaved work — they need to know that saving is what
  // lets it through, and that leaving now costs them the changes.
  const isDeferredBlock = !!pendingBlockPolicy;
  const noticeOpen =
    (noticeAvailable && !noticeDismissed) || (isDeferredBlock && !deferredNoticeDismissed);

  return (
    <>
      <ConfirmDialog
        open={noticeOpen}
        onClose={closeModal}
        onConfirm={refreshCacheAndReload}
        icon={<RefreshIcon />}
        title={isDeferredBlock ? dialogs.mandatoryUpdatePendingTitle : dialogs.updateRequired}
        description={
          isDeferredBlock ? dialogs.mandatoryUpdatePendingSubtitle : dialogs.updateVersionSubtitle
        }
        confirmLabel={isDeferredBlock ? dialogs.mandatoryUpdatePendingConfirm : dialogs.updateNow}
        cancelLabel={isDeferredBlock ? dialogs.mandatoryUpdatePendingCancel : dialogs.skipNow}
      />
      {children}
    </>
  );
};

export default CacheBuster;
